import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import type { DsrcDeclaration } from './types';
import { DeclarationAssessmentsbox } from './DeclarationAssessmentsbox';
import { PAGE_TEXT } from '../../../../lang';
import {
  useCreateDsrcDeclaration,
  useGetDsrcDeclaration,
} from '../../../../graphql';
import {
  Accordion,
  Loading,
  Alert,
  type AlertDetails,
} from '../../../../../../components';
import { type ApplicationRemarkType } from '../../../../../Nomination/graphql';

type DeclarationAssessmentsProps = {
  isViewMode?: boolean;
  recommendForHKPFS: boolean;
  returnedRemarks: ApplicationRemarkType[];
  fundingSchema?: string;
  fundingSource?: string;
  refetchAssessment?: () => void;
};

export const DeclarationAssessments: React.FunctionComponent<
  DeclarationAssessmentsProps
> = (props: DeclarationAssessmentsProps) => {
  const STEP_TEXT = PAGE_TEXT.DRC_ASSESSMENT_DETAILS.ASSESSMENT.STEP_H;
  const { applicationId = '' } = useParams();
  const {
    isViewMode,
    refetchAssessment = () => {},
    recommendForHKPFS,
    fundingSchema,
    fundingSource,
    returnedRemarks,
  } = props;

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const {
    dsrcDeclaration: initialValues,
    stipendBudgetDeclarationRequired,
    loading,
    error: dsrcDeclarationError,
  } = useGetDsrcDeclaration(applicationId);

  const createDsrcDeclaration = useCreateDsrcDeclaration();
  const handleSubmit = async (value: DsrcDeclaration): Promise<void> => {
    try {
      const variables = {
        input: { applicationId, ...value },
      };
      await createDsrcDeclaration({
        variables,
      });
      refetchAssessment();
      setAlertDetails({
        alertContent: 'Saved Declaration Successfully.',
        alertType: 'success',
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: any) {
      setAlertDetails({
        alertContent: 'Saved Declaration Failed.' + (error as Error).message,
        alertType: 'error',
      });
    }
    setOpenAlert(true);
  };

  if (dsrcDeclarationError) {
    return (
      <Alert
        openAlert
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            'Error Occurs.' + (dsrcDeclarationError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  return (
    <ContentBox>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {loading ? (
        <Loading />
      ) : (
        <Accordion
          className="w-full"
          defaultExpanded
          disableExpand
          title={STEP_TEXT.TITLE}
          details={
            <DeclarationAssessmentsbox
              isViewMode={isViewMode}
              initialValues={initialValues}
              returnedRemarks={returnedRemarks}
              fundingSchema={fundingSchema}
              fundingSource={fundingSource}
              stipendBudgetRequired={stipendBudgetDeclarationRequired}
              recommendForHKPFS={recommendForHKPFS}
              onSubmit={handleSubmit}
            />
          }
        />
      )}
    </ContentBox>
  );
};

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;
